import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Box,
  TextField,
  Autocomplete,
  Grid,
  Typography,
  Button,
} from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import Cookie from "js-cookie";
import { StyledCancelButton, StyledButton } from "components/StyledComponents/styledComponents";
import URLs from "constants/urls";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

export default function AdditionalPayoutsDialog({
  open,
  onClose,
  startDate,
  endDate,
  selectedDsaId,
}) {
  const [rowData, setRowData] = useState([]);
  const [filteredRowData, setFilteredRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pinnedBottomRowData, setPinnedBottomRowData] = useState([]);
  const [searchColumn, setSearchColumn] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [columnOptions, setColumnOptions] = useState([]);
  const jwt_token = Cookie.get("jwt_token");
  const gridRef = useRef();

  const formatIndianCurrency = (value) => {
    if (value == null) return "";
    return Number(value).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Define all columns
  const allColumnDefs = useMemo(
    () => [
      {
        headerName: "Invoice No",
        field: "invoiceNo",
        sortable: true,
        filter: true,
        flex: 1,
        cellStyle: (params) => ({
          textAlign: "left",
          fontWeight:
            params.data?.invoiceNo === "Total" || params.data?.invoiceNo === "Total (Filtered)"
              ? "bold"
              : "500",
        }),
        valueGetter: (params) => {
          // Ensure footer shows text, not "Invalid Number"
          if (params.node.rowPinned) {
            return params.data?.invoiceNo || "";
          }
          return params.data?.invoiceNo;
        },
      },
      {
        headerName: "Additional Payout",
        field: "additionalPayout",
        sortable: true,
        filter: true,
        valueFormatter: (params) => formatIndianCurrency(params.value),
        cellStyle: (params) => ({
          textAlign: "right",
          fontWeight:
            params.data?.invoiceNo === "Total" || params.data?.invoiceNo === "Total (Filtered)"
              ? "bold"
              : "500",
        }),
        flex: 1,
        type: "numericColumn",
        aggFunc: "sum",
      },
      {
        headerName: "Remarks",
        field: "remarks",
        sortable: true,
        filter: true,
        cellStyle: (params) => ({
          textAlign: "left",
          fontWeight:
            params.data?.invoiceNo === "Total" || params.data?.invoiceNo === "Total (Filtered)"
              ? "bold"
              : "500",
        }),
        flex: 2,
      },
    ],
    []
  );

  // Initialize column options for search
  useEffect(() => {
    const options = allColumnDefs
      .filter((col) => col.field)
      .map((col) => ({
        label: col.headerName,
        value: col.field,
        field: col.field,
        type: col.type || "string",
      }));
    setColumnOptions(options);

    // Auto-select the first column (Invoice No) by default
    if (options.length > 0 && !searchColumn) {
      setSearchColumn(options[0]);
    }
  }, [allColumnDefs, searchColumn]);

  // Filter data based on search
  useEffect(() => {
    if (!searchText.trim() || !searchColumn) {
      setFilteredRowData(rowData);
      return;
    }

    const lowerSearchText = searchText.toLowerCase();
    const filtered = rowData.filter((row) => {
      const fieldValue = row[searchColumn.field];
      if (fieldValue == null) return false;

      // Handle numeric fields differently
      if (searchColumn.type === "numericColumn" || typeof fieldValue === "number") {
        return fieldValue.toString().includes(searchText);
      }

      return fieldValue.toString().toLowerCase().includes(lowerSearchText);
    });

    setFilteredRowData(filtered);
  }, [rowData, searchColumn, searchText]);

  // Calculate footer totals for filtered rows
  const calculateFooterTotals = useCallback((data) => {
    if (!data || data.length === 0) {
      return {
        additionalPayout: 0,
      };
    }

    return {
      additionalPayout: data.reduce((sum, row) => sum + (row.additionalPayout || 0), 0),
    };
  }, []);

  // Update footer when data or filter changes
  useEffect(() => {
    const dataToUse = searchText && searchColumn ? filteredRowData : rowData;
    const totals = calculateFooterTotals(dataToUse);

    const footerRow = {
      invoiceNo: searchText && searchColumn ? "Total (Filtered)" : "Total (Filtered)",
      additionalPayout: totals.additionalPayout,
    };

    setPinnedBottomRowData([footerRow]);
  }, [rowData, filteredRowData, searchText, searchColumn, calculateFooterTotals]);

  // Handle search input changes
  const handleSearchColumnChange = (event, newValue) => {
    setSearchColumn(newValue);
    if (!newValue) {
      setSearchText("");
    }
  };

  const handleSearchTextChange = (event) => {
    setSearchText(event.target.value);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchColumn(columnOptions[0] || null);
    setSearchText("");
  };

  // Handle Excel download
  const handleDownloadExcel = () => {
    const dataToExport = searchText && searchColumn ? filteredRowData : rowData;

    if (!dataToExport || dataToExport.length === 0) return;

    const allData = [...dataToExport, ...pinnedBottomRowData];

    // Re-map to readable column structure
    const exportData = allData.map((row) => ({
      "Invoice No": row.invoiceNo || "",
      "Additional Payout": row.additionalPayout || 0,
      Remarks: row.remarks || "",
    }));

    // Create worksheet & workbook
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Additional Payouts");

    // Auto column width
    const maxWidths = [];
    exportData.forEach((row) => {
      Object.values(row).forEach((val, i) => {
        const len = val ? val.toString().length : 10;
        maxWidths[i] = Math.max(maxWidths[i] || 10, len);
      });
    });
    worksheet["!cols"] = maxWidths.map((w) => ({ wch: w + 2 }));

    // Write to file
    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([wbout], { type: "application/octet-stream" }),
      `Additional_Payouts_${startDate}_to_${endDate}.xlsx`
    );
  };

  // Fetch data
  useEffect(() => {
    if (!open) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const url = `${
          URLs.additionalpayoutsBrekdownUrl
        }?startDate=${startDate}&endDate=${endDate}&companyId=${selectedDsaId ?? 0}`;
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt_token}`,
          },
        });

        if (!response.ok) throw new Error("Network response was not ok");
        const data = await response.json();

        setRowData(data);
        setFilteredRowData(data);
      } catch (error) {
        console.error("Error fetching additional payouts:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [open, startDate, endDate, selectedDsaId, jwt_token]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Additional Payouts</DialogTitle>

      {/* Search Controls */}
      <DialogContent style={{ padding: "0 24px" }}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2, mt: 0.1 }}>
          <Grid item xs={12} sm={4}>
            <Autocomplete
              options={columnOptions}
              getOptionLabel={(option) => option.label}
              value={searchColumn}
              onChange={handleSearchColumnChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Column"
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              )}
              disableClearable={false}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Search Text"
              variant="outlined"
              size="small"
              value={searchText}
              onChange={handleSearchTextChange}
              placeholder={
                searchColumn ? `Search in ${searchColumn.label}...` : "Select a column first"
              }
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="outlined"
              onClick={handleClearSearch}
              disabled={!searchText}
              sx={{ mr: 1, color: "#221f1f" }}
            >
              Clear Search
            </Button>
            <Typography variant="caption" color="#221f1f">
              {searchText && searchColumn
                ? `Showing ${filteredRowData.length} of ${rowData.length} rows`
                : `Total ${rowData.length} rows`}
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>

      {/* Grid Content */}
      <DialogContent style={{ height: 400, position: "relative", paddingTop: 0 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <div className="ag-theme-alpine" style={{ height: "100%", width: "100%" }}>
            <AgGridReact
              ref={gridRef}
              rowData={searchText && searchColumn ? filteredRowData : rowData}
              columnDefs={allColumnDefs}
              pinnedBottomRowData={pinnedBottomRowData}
              defaultColDef={{
                resizable: true,
                flex: 1,
                minWidth: 120,
              }}
              getRowStyle={(params) => {
                if (params.node.rowPinned === "bottom") {
                  return {
                    fontWeight: "bold",
                    backgroundColor: "#f5f6f8",
                  };
                }
                return null;
              }}
              pagination
              paginationPageSize={100}
              suppressAggFuncInHeader
            />
          </div>
        )}
      </DialogContent>

      <DialogActions>
        <StyledButton
          variant="contained"
          color="primary"
          onClick={handleDownloadExcel}
          sx={{ textTransform: "none" }}
          disabled={rowData.length === 0}
        >
          Download Excel
        </StyledButton>
        <StyledCancelButton onClick={onClose} variant="outlined">
          Close
        </StyledCancelButton>
      </DialogActions>
    </Dialog>
  );
}

AdditionalPayoutsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  selectedDsaId: PropTypes.number,
};
